import { useMarketingImage } from "byzantine";
import { useState } from "react";
import type { WebSectionId } from "byzantine/src/dbbl/businessLogic/modules/userDisplayPreferences/types";
import { SECTION } from "byzantine/src/dbbl/businessLogic/modules/userDisplayPreferences/constants";
import { useDashboardHiddenSections } from "./contexts/DashboardHiddenSectionsContext";
import {
  MARKETING_CARD_IMAGE_ADDRESS,
  MARKETING_BANNER_IMAGE_ADDRESS,
  LARGE_MARKETING_IMAGE_ADDRESS,
  LARGE_MARKETING_BANNER_IMAGE_ADDRESS
} from "./dashboard/constants";
import type { ImagePlacementAddress } from "./dashboard/MarketingImage/MarketingImage";

const placementAttributesByAddress: Record<
  ImagePlacementAddress,
  {
    width: number;
    height: number;
    containerClassName?: string;
  }
> = {
  [MARKETING_CARD_IMAGE_ADDRESS]: {
    width: 300,
    height: 250,
    containerClassName:
      "dashboard-sidebar-card-image alignChild--center--center"
  },
  [MARKETING_BANNER_IMAGE_ADDRESS]: {
    width: 728,
    height: 90,
    containerClassName: "dashboard-banner-image alignChild--center--center "
  },
  [LARGE_MARKETING_IMAGE_ADDRESS]: {
    width: 1260,
    height: 840,
    containerClassName: "dashboard-marketing-image"
  },
  [LARGE_MARKETING_BANNER_IMAGE_ADDRESS]: {
    width: 1456,
    height: 180,
    containerClassName: "dashboard-marketing-image"
  }
};

const getPlacementSizeByAddress = (address: ImagePlacementAddress) =>
  placementAttributesByAddress[address];

const sectionIdByAddress: Record<ImagePlacementAddress, WebSectionId> = {
  [MARKETING_BANNER_IMAGE_ADDRESS]: SECTION.MARKETING_BANNER_IMAGE,
  [LARGE_MARKETING_BANNER_IMAGE_ADDRESS]: SECTION.MARKETING_BANNER_IMAGE,
  [MARKETING_CARD_IMAGE_ADDRESS]: SECTION.MARKETING_CARD_IMAGE,
  [LARGE_MARKETING_IMAGE_ADDRESS]: SECTION.MARKETING_CARD_IMAGE
};

export default function MarketingImageTargetWeb({
  address,
  // onImageLoad is called when the image successfully loads.
  onImageLoad,
  // onImageError is called if the image does not load. Note that this may be part of normal operation,
  // for example if there is no ad to serve.
  onImageError
}: {
  address: ImagePlacementAddress;
  onImageLoad?: () => void;
  onImageError?: () => void;
}) {
  const [isError, setIsError] = useState(false);
  const { setSectionHidden } = useDashboardHiddenSections();
  const { width, height, containerClassName } =
    getPlacementSizeByAddress(address);
  const debugSize = { width, height };
  const sectionId = sectionIdByAddress[address];
  const { linkUrl, imageUrl, segmintIframeUrl, opensInNewTab } =
    useMarketingImage(address, debugSize);

  if (isError) {
    return null;
  }

  return (
    <div className={containerClassName}>
      <iframe
        src={segmintIframeUrl}
        style={{ width: 0, height: 0, border: 0, position: "absolute" }}
      />
      <a
        href={linkUrl}
        target={opensInNewTab ? "_blank" : "_self"}
        rel="noopener noreferrer"
      >
        <img
          src={imageUrl}
          onLoad={onImageLoad}
          onError={() => {
            setIsError(true);
            setSectionHidden(sectionId, true);
            onImageError?.();
          }}
          // TODO: this may become dynamic in the future
          alt="Promotional image"
        />
      </a>
    </div>
  );
}
